import { useCallback, useEffect, useState } from "react";
import { listCategorizations, undoCategorization } from "../api/transactions";
import type { PostedRow } from "../types/account";
import type { Categorization } from "../types/transaction";
import { asRefusal } from "./useServerAnswer";

// The review list's largest page; the personal move's rows are found in it.
const REVIEW_PAGE = 200;

export interface PostedRowRemoval {
  /** True when the row's review entry offers its undo. */
  canRemove: (transactionId: number) => boolean;
  /** Undo the row's review entry with the log line it was shown with. */
  remove: (transactionId: number) => Promise<void>;
  removed: ReadonlySet<number>;
  /** The server's sentence of the last refused removal, else "". */
  refusal: string;
}

/**
 * Remove for the personal move's listed rows: the review row's own undo.
 *
 * A posted row carries its transaction id only, so the review list is read
 * once to find each row's entry; Remove is offered only where that entry has
 * `can_undo`, and sends its `last_log_id` as `expected_last_log_id`.
 */
export function usePostedRowRemoval(
  clientId: number,
  rows: PostedRow[],
  onChanged: () => void,
): PostedRowRemoval {
  const [entries, setEntries] = useState<Map<number, Categorization>>(new Map());
  const [removed, setRemoved] = useState<Set<number>>(new Set());
  const [refusal, setRefusal] = useState("");
  const wanted = rows.length > 0;

  useEffect(() => {
    if (!wanted) return;
    let cancelled = false;
    listCategorizations(clientId, undefined, REVIEW_PAGE, 0)
      .then((list) => {
        if (cancelled) return;
        setEntries(new Map(list.items.map((item) => [item.transaction_id, item])));
      })
      .catch((err: unknown) => {
        if (!cancelled) setRefusal(asRefusal(err).message);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, wanted]);

  const canRemove = useCallback(
    (transactionId: number) => entries.get(transactionId)?.can_undo === true,
    [entries],
  );

  const remove = useCallback(
    async (transactionId: number) => {
      const entry = entries.get(transactionId);
      if (!entry) return;
      setRefusal("");
      try {
        await undoCategorization(clientId, entry.id, entry.last_log_id);
        setRemoved((prev) => new Set(prev).add(transactionId));
        onChanged();
      } catch (err) {
        setRefusal(asRefusal(err).message);
      }
    },
    [clientId, entries, onChanged],
  );

  return { canRemove, remove, removed, refusal };
}
